import { Sale, SaleItem, Payment, Customer, Plant } from '../types';

// Cache for logo base64 data
let logoBase64Cache: string | null = null;

// Function to load and cache logo as base64
export const loadLogoAsBase64 = (): Promise<string> => {
  return new Promise((resolve) => {
    // Return cached version if available
    if (logoBase64Cache) {
      resolve(logoBase64Cache);
      return;
    }

    const img = new Image();
    // Don't set crossOrigin for same-origin images
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          logoBase64Cache = canvas.toDataURL('image/png');
          resolve(logoBase64Cache);
        } else {
          resolve('');
        }
      } catch (e) {
        console.error('Error converting logo to base64:', e);
        resolve('');
      }
    };
    img.onerror = (e) => {
      console.error('Error loading logo image:', e);
      resolve('');
    };
    // Use PUBLIC_URL to handle subdirectory deployments (e.g., /polyhouse/)
    const publicUrl = process.env.PUBLIC_URL || '';
    img.src = window.location.origin + publicUrl + '/nursery_logo.png';
  });
};

// Helper to get order items (handles legacy orders)
const getOrderItemsForReceipt = (order: Sale, plantDetails?: Plant): SaleItem[] => {
  // If order has items array, return it
  if (order.items && order.items.length > 0) {
    return order.items;
  }

  // Convert legacy single-plant order to items array format
  if (order.plantId || (order as any).plantName) {
    const legacyItem: SaleItem = {
      id: `legacy-${order.id}`,
      plantId: order.plantId || '',
      plantName: plantDetails?.name || (order as any).plantName || '-',
      plantBrand: plantDetails?.brand || (order as any).plantBrand,
      plantVariety: plantDetails?.variety || (order as any).plantVariety,
      pricePerPlant: plantDetails?.pricePerPlant || (order as any).pricePerPlant || 0,
      quantity: (order as any).quantity || order.totalTrays * ((order as any).seedsPerTray || plantDetails?.seedsPerTray || 126),
      totalPrice: order.totalAmount,
      seedsPerTray: (order as any).seedsPerTray || plantDetails?.seedsPerTray || 126,
      traysRequired: order.totalTrays,
    };
    return [legacyItem];
  }

  return [];
};

// Get contact info from localStorage
export const getContactInfo = () => {
  const defaultInfo = {
    primaryPhone: '+91 9424699693',
    secondaryPhone: '+91 6263930077',
    email: 'kariyasnursery@gmail.com',
    address: 'Umranala, Chhindwara, Madhya Pradesh, 480107',
  };

  try {
    const saved = localStorage.getItem('kariyasnursery_contact');
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        primaryPhone: parsed.primaryPhone || parsed.phone || defaultInfo.primaryPhone,
        secondaryPhone: parsed.secondaryPhone || defaultInfo.secondaryPhone,
        email: parsed.email || defaultInfo.email,
        address: parsed.address || defaultInfo.address,
      };
    }
  } catch {
    // Return default if parsing fails
  }
  return defaultInfo;
};

export const generateReceiptNumber = () => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000);
  return `RCP-${timestamp}-${random}`;
};

export const formatDate = (timestamp: any) => {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const formatCurrency = (amount: number) => {
  return `₹${amount.toLocaleString('en-IN')}`;
};

export const getStatusText = (status: any) => {
  return status?.name || 'Pending';
};

export const printReceipt = (receiptContent: string, receiptNumber: string) => {
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt ${receiptNumber} - Kariyas Nursery</title>
          <script>
            function waitForImageAndPrint() {
              var img = document.querySelector('.company-logo');
              if (img) {
                if (img.complete && img.naturalHeight !== 0) {
                  window.print();
                  window.close();
                } else {
                  img.onload = function() {
                    window.print();
                    window.close();
                  };
                  img.onerror = function() {
                    // Print anyway if image fails to load
                    window.print();
                    window.close();
                  };
                  // Fallback timeout in case onload doesn't fire
                  setTimeout(function() {
                    window.print();
                    window.close();
                  }, 2000);
                }
              } else {
                window.print();
                window.close();
              }
            }
          </script>
          <style>
            * {
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              margin: 0;
              padding: 0;
              background: white;
              color: #333;
              line-height: 1.4;
              font-size: 12px;
            }
            
            .receipt {
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
              background: white;
            }
            
            .header {
              text-align: center;
              border-bottom: 2px solid #16a34a;
              padding-bottom: 15px;
              margin-bottom: 20px;
              position: relative;
            }
            
            .header::after {
              content: '';
              position: absolute;
              bottom: -2px;
              left: 50%;
              transform: translateX(-50%);
              width: 40px;
              height: 2px;
              background: #dc2626;
            }
            
            .header-brand {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
              margin-bottom: 10px;
            }

            .company-logo {
              height: 45px;
              width: 45px;
              object-fit: contain;
            }

            .brand-text {
              text-align: left;
            }

            .company-name {
              font-size: 20px;
              font-weight: bold;
              color: #15803d;
              margin: 0;
              text-transform: uppercase;
              letter-spacing: 1px;
              line-height: 1.2;
            }

            .company-tagline {
              color: #6b7280;
              margin: 2px 0 0 0;
              font-size: 10px;
            }
            
            .receipt-number {
              background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
              padding: 8px 15px;
              border-radius: 6px;
              margin: 10px 0;
              border: 1px solid #d1d5db;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            }
            
            .receipt-number strong {
              color: #374151;
              font-size: 14px;
            }
            
            .receipt-title {
              font-size: 16px;
              font-weight: bold;
              color: #1f2937;
              margin: 8px 0 3px 0;
            }
            
            .receipt-date {
              color: #6b7280;
              font-size: 12px;
              margin: 0;
            }
            
            .section {
              margin-bottom: 15px;
              border-bottom: 1px solid #e5e7eb;
              padding-bottom: 12px;
            }
            
            .section:last-child {
              border-bottom: none;
            }
            
            .section h3 {
              color: #1f2937;
              margin-bottom: 8px;
              font-size: 14px;
              font-weight: 600;
              border-left: 3px solid #16a34a;
              padding-left: 8px;
            }
            
            .grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 15px;
            }
            
            .detail-group {
              margin-bottom: 10px;
            }
            
            .detail-group h4 {
              color: #374151;
              margin: 0 0 5px 0;
              font-size: 13px;
              font-weight: 600;
            }
            
            .detail-item {
              margin: 3px 0;
              font-size: 11px;
              color: #4b5563;
            }
            
            .detail-item strong {
              color: #1f2937;
            }
            
            .product-info {
              background: #f9fafb;
              padding: 8px;
              border-radius: 4px;
              margin-top: 8px;
              border: 1px solid #e5e7eb;
            }
            
            .product-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 4px;
              font-size: 11px;
            }
            
            .product-row:last-child {
              margin-bottom: 0;
              border-top: 1px solid #e5e7eb;
              padding-top: 4px;
              font-weight: bold;
            }
            
            .amount-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 6px;
              padding: 4px 0;
              font-size: 11px;
            }
            
            .amount-row.balance {
              background: #f3f4f6;
              padding: 6px;
              border-radius: 4px;
              margin-top: 8px;
              font-weight: bold;
              font-size: 12px;
            }
            
            .payment-item {
              background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
              padding: 8px;
              border-radius: 4px;
              margin-bottom: 6px;
              border: 1px solid #e5e7eb;
              box-shadow: 0 1px 2px rgba(0,0,0,0.1);
            }
            
            .payment-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 4px;
            }
            
            .payment-amount {
              font-size: 14px;
              font-weight: bold;
              color: #16a34a;
            }
            
            .payment-method {
              color: #6b7280;
              font-size: 10px;
              margin-top: 2px;
            }
            
            .footer {
              text-align: center;
              margin-top: 20px;
              padding-top: 15px;
              border-top: 1px solid #e5e7eb;
              background: #f9fafb;
              padding: 12px;
              border-radius: 6px;
            }
            
            .footer p {
              margin: 3px 0;
            }
            
            .thank-you {
              color: #16a34a;
              font-weight: 600;
              font-size: 13px;
              margin-bottom: 6px;
            }
            
            .contact-info {
              color: #6b7280;
              font-size: 11px;
            }
            
            @page {
              size: auto;
              margin: 10mm;
            }

            @media print {
              html, body {
                height: auto;
              }
              body {
                margin: 0;
                padding: 0;
                font-size: 10px;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .receipt {
                border: none;
                box-shadow: none;
                max-width: none;
                margin: 0;
                padding: 15px;
              }
              .header::after {
                display: none;
              }
              .product-info {
                box-shadow: none;
                border: 1px solid #ddd;
              }
              .payment-item {
                box-shadow: none;
                border: 1px solid #ddd;
              }
              .section {
                margin-bottom: 10px;
                padding-bottom: 8px;
              }
            }
          </style>
        </head>
        <body>
          ${receiptContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    // Wait for image to load before printing
    (printWindow as any).waitForImageAndPrint();
  }
};

export const generateReceiptHTML = (
  order: Sale,
  customer: Customer,
  payments: Payment[] = [],
  receiptType: 'order' | 'payment' | 'completion',
  plantDetails?: Plant,
  logoBase64?: string
): { html: string; receiptNumber: string } => {
  const receiptNumber = generateReceiptNumber();
  const currentDate = new Date().toLocaleDateString('en-IN');
  const currentTime = new Date().toLocaleTimeString('en-IN');
  const contact = getContactInfo();
  // Use base64 logo if available, otherwise fall back to URL
  const logoUrl = logoBase64 || `${window.location.origin}/nursery_logo.png`;

  const getReceiptTitle = () => {
    switch (receiptType) {
      case 'order':
        return 'ORDER RECEIPT';
      case 'payment':
        return 'PAYMENT RECEIPT';
      case 'completion':
        return 'COMPLETION RECEIPT';
      default:
        return 'RECEIPT';
    }
  };

  const html = `
    <div class="receipt">
      <div class="header">
        <div class="header-brand">
          ${logoUrl ? `<img src="${logoUrl}" alt="Kariyas Nursery Logo" class="company-logo" onerror="this.style.display='none'" />` : ''}
          <div class="brand-text">
            <h1 class="company-name">Kariyas Nursery</h1>
            <p class="company-tagline">Premium & High Quality Plants</p>
          </div>
        </div>
        <div class="receipt-number">
          <strong>Receipt No: ${receiptNumber}</strong>
        </div>
        <div>
          <p class="receipt-title">${getReceiptTitle()}</p>
          <p class="receipt-date">Date: ${currentDate} | Time: ${currentTime}</p>
        </div>
      </div>

      <div class="section">
        <h3>Contact Details</h3>
        <div class="grid">
          <div class="detail-group">
            <h4>Kariyas Nursery</h4>
            <div class="detail-item">📍 ${contact.address}</div>
            <div class="detail-item">📞 ${contact.primaryPhone} | ${contact.secondaryPhone}</div>
            <div class="detail-item">✉️ ${contact.email}</div>
          </div>
          <div class="detail-group">
            <h4>Customer</h4>
            <div class="detail-item"><strong>Name:</strong> ${customer.name}</div>
            <div class="detail-item"><strong>Phone:</strong> ${customer.phone}</div>
            <div class="detail-item"><strong>Address:</strong> ${customer.address}</div>
            ${customer.distanceKm ? `<div class="detail-item"><strong>Distance:</strong> ${customer.distanceKm}km from polyhouse</div>` : ''}
          </div>
        </div>
      </div>

      <div class="section">
        <h3>Order Details</h3>
        <div class="grid">
          <div class="detail-group">
            <div class="detail-item"><strong>Order ID:</strong> #${order.orderId || order.id}</div>
            <div class="detail-item"><strong>Order Date:</strong> ${formatDate(order.orderDate)}</div>
            <div class="detail-item"><strong>Status:</strong> ${getStatusText(order.status)}</div>
          </div>
          <div class="detail-group">
            <div class="detail-item"><strong>Delivery Date:</strong> ${formatDate((order as any).expectedDeliveryDate || order.deliveryDate)}</div>
            <div class="detail-item"><strong>Total Trays:</strong> ${order.totalTrays}</div>
            <div class="detail-item"><strong>Total Plants:</strong> ${(() => {
              const items = getOrderItemsForReceipt(order, plantDetails);
              return items.reduce((sum, item) => sum + item.quantity, 0);
            })()}</div>
          </div>
        </div>

        <div class="product-info">
          <h4 style="margin: 0 0 8px 0; font-size: 12px; color: #374151;">Order Items</h4>
          ${(() => {
            const items = getOrderItemsForReceipt(order, plantDetails);
            if (items.length === 1) {
              // Single item display (legacy style)
              const item = items[0];
              return `
                <div class="product-row">
                  <span><strong>Plant Name:</strong> ${item.plantName}</span>
                  <span><strong>Brand:</strong> ${item.plantBrand || '-'}</span>
                </div>
                <div class="product-row">
                  <span><strong>Variety:</strong> ${item.plantVariety || '-'}</span>
                  <span><strong>Price:</strong> ${formatCurrency(item.pricePerPlant)} per plant</span>
                </div>
                <div class="product-row">
                  <span><strong>Quantity:</strong> ${item.quantity.toLocaleString()} plants</span>
                  <span><strong>Total:</strong> ${formatCurrency(item.totalPrice)}</span>
                </div>
              `;
            } else {
              // Multi-item table display
              return `
                <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
                  <thead>
                    <tr style="background: #f3f4f6;">
                      <th style="padding: 6px; text-align: left; border-bottom: 1px solid #e5e7eb;">Plant</th>
                      <th style="padding: 6px; text-align: right; border-bottom: 1px solid #e5e7eb;">Qty</th>
                      <th style="padding: 6px; text-align: right; border-bottom: 1px solid #e5e7eb;">Rate</th>
                      <th style="padding: 6px; text-align: right; border-bottom: 1px solid #e5e7eb;">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${items.map(item => `
                      <tr>
                        <td style="padding: 6px; border-bottom: 1px solid #e5e7eb;">
                          ${item.plantName}
                          ${item.plantBrand || item.plantVariety ? `<br><span style="color: #6b7280; font-size: 10px;">${item.plantBrand || ''}${item.plantVariety ? ` - ${item.plantVariety}` : ''}</span>` : ''}
                        </td>
                        <td style="padding: 6px; text-align: right; border-bottom: 1px solid #e5e7eb;">${item.quantity.toLocaleString()}</td>
                        <td style="padding: 6px; text-align: right; border-bottom: 1px solid #e5e7eb;">${formatCurrency(item.pricePerPlant)}</td>
                        <td style="padding: 6px; text-align: right; border-bottom: 1px solid #e5e7eb;">${formatCurrency(item.totalPrice)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                  <tfoot>
                    <tr style="background: #f3f4f6; font-weight: bold;">
                      <td style="padding: 6px;">Total</td>
                      <td style="padding: 6px; text-align: right;">${items.reduce((sum, item) => sum + item.quantity, 0).toLocaleString()}</td>
                      <td style="padding: 6px;"></td>
                      <td style="padding: 6px; text-align: right;">${formatCurrency(items.reduce((sum, item) => sum + item.totalPrice, 0))}</td>
                    </tr>
                  </tfoot>
                </table>
              `;
            }
          })()}
        </div>
      </div>

      <div class="section">
        <h3>Amount Details</h3>
        <div class="amount-row">
          <span>Total Amount:</span>
          <span><strong>${formatCurrency(order.totalAmount)}</strong></span>
        </div>
        <div class="amount-row">
          <span>Amount Paid:</span>
          <span style="color: #16a34a;"><strong>${formatCurrency(order.amountPaid)}</strong></span>
        </div>
        <div class="amount-row">
          <span>Due Amount:</span>
          <span style="color: #dc2626;"><strong>${formatCurrency(order.remainingAmount)}</strong></span>
        </div>
        <div class="amount-row balance">
          <span><strong>Balance:</strong></span>
          <span style="${order.remainingAmount > 0 ? 'color: #dc2626' : 'color: #16a34a'};">
            <strong>${order.remainingAmount > 0 ? `Due: ${formatCurrency(order.remainingAmount)}` : 'Paid in Full'}</strong>
          </span>
        </div>
      </div>

      ${payments.length > 0 ? `
        <div class="section">
          <h3>Payment History</h3>
          ${payments.map((payment, index) => `
            <div class="payment-item">
              <div class="payment-header">
                <div>
                  <strong>Payment ${index + 1}</strong>
                  <div class="detail-item">${formatDate(payment.paymentDate)}</div>
                </div>
                <div style="text-align: right;">
                  <div class="payment-amount">${formatCurrency(payment.amount)}</div>
                  <div class="payment-method">${payment.paymentMethod}</div>
                </div>
              </div>
            </div>
          `).join('')}
        </div>
      ` : ''}

      <div class="footer">
        <p class="thank-you">Thank you for choosing Kariyas Nursery!</p>
        <p class="contact-info">
          For any queries, please contact us at ${contact.primaryPhone} | ${contact.secondaryPhone}
        </p>
        <p class="contact-info" style="margin-top: 4px;">
          ✉️ ${contact.email}
        </p>
        <p class="contact-info" style="margin-top: 6px; font-size: 10px;">
          Quality plants, professional service, sustainable growth
        </p>
      </div>
    </div>
  `;

  return { html, receiptNumber };
};

// Async function to print receipt with embedded logo
export const printReceiptWithLogo = async (
  order: Sale,
  customer: Customer,
  payments: Payment[] = [],
  receiptType: 'order' | 'payment' | 'completion',
  plantDetails?: Plant
): Promise<void> => {
  // Load logo as base64 first
  const logoBase64 = await loadLogoAsBase64();

  // Generate receipt HTML with embedded logo
  const { html, receiptNumber } = generateReceiptHTML(
    order,
    customer,
    payments,
    receiptType,
    plantDetails,
    logoBase64
  );

  // Print the receipt
  printReceipt(html, receiptNumber);
}; 